import { ReminderListItem } from 'models/reminders'
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns'

interface MobileReminderItemProps {
  reminder: ReminderListItem
}

/**
 * Formats the reminder time for display.
 * Shows "Tomorrow, 10:15 am" or "Thursday, 4:15 pm" etc.
 */
function formatReminderTime(reminder: ReminderListItem): string {
  const dateStr = reminder.next_run_at || reminder.run_at
  if (!dateStr) return ''

  const date = new Date(dateStr)

  // For recurring reminders, show day of week + time (e.g., "Thursdays, 2:15 pm")
  if (reminder.reminder_type !== 'one_off') {
    const dayOfWeek = format(date, 'EEEE') + 's' // Add 's' for plural
    const time = format(date, 'h:mm a').toLowerCase()
    return `${dayOfWeek}, ${time}`
  }

  // For one-time reminders
  if (isToday(date)) {
    return `Today, ${format(date, 'h:mm a').toLowerCase()}`
  }

  if (isTomorrow(date)) {
    return `Tomorrow, ${format(date, 'h:mm a').toLowerCase()}`
  }

  if (isThisWeek(date)) {
    return `${format(date, 'EEEE')}, ${format(date, 'h:mm a').toLowerCase()}`
  }

  // For dates further out
  return `${format(date, 'MMM d')}, ${format(date, 'h:mm a').toLowerCase()}`
}

export function MobileReminderItem({ reminder }: MobileReminderItemProps) {
  const time = formatReminderTime(reminder)

  return (
    <div className="flex items-center gap-3 py-2 px-1">
      {/* Green dot indicator */}
      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-green-500" />

      {/* Title */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 truncate">{reminder.title}</p>
      </div>

      {/* Time */}
      <div className="flex-shrink-0">
        <span className="text-xs text-gray-400">{time}</span>
      </div>
    </div>
  )
}
